import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

export default function InstantSuccess() {
  const location = useLocation();

  const storeNumber = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (params.get("store") || params.get("storeNumber") || "").trim();
  }, [location.search]);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [website, setWebsite] = useState(""); // honeypot

  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });

  // ✅ Point this to your Master_Function URL
  // Example: https://<YOUR-FUNCTION-APP>.azurewebsites.net/api/Master_Function
  const MASTER_FUNCTION_URL = "https://rgs-a.azurewebsites.net/api/Master_Function";

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    // Bot honeypot
    if (website && website.trim().length > 0) {
      // silently ignore bots
      return;
    }

    if (!storeNumber) {
      setStatus({
        type: "error",
        message: "❌ Store information is missing from the link. Please rescan the QR code.",
      });
      return;
    }

    setSubmitting(true);
    setStatus({ type: "", message: "" });

    try {
      const detailsObj = {
        title: "Instant Access Help",
        name: name.trim(),
        email: email.trim(),
        phone: (phone || "").trim(),
        note: (note || "").trim(),
        page: window.location.href,
        submittedAt: new Date().toISOString(),
        context: "openpath_email_not_received",
      };

      // ✅ Master_Function routes to Message_Logging
      // This matches your schema:
      // storeNumber, memberId, action, result, details, approvedBy, timeStampt, messageType, severity, source
      const body = {
        target: "Message_Logging",
        payload: {
          storeNumber,
          memberId: "", // optional: populate if you have it here
          action: "InstantAccessHelp",
          result: "Pending",
          details: JSON.stringify(detailsObj),
          messageType: "Help Ticket",
          severity: "Critical",
          source: "System",
        },
      };

      const res = await fetch(MASTER_FUNCTION_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.message || "Request failed");
      }

      setStatus({
        type: "success",
        message: "✅ Request received. A manager has been notified and will reach out soon.",
      });

      setName("");
      setEmail("");
      setPhone("");
      setNote("");
      setWebsite("");
    } catch (err) {
      setStatus({
        type: "error",
        message:
          "❌ Unable to send request right now. Please contact your store directly or try again in a few minutes.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <h1 style={styles.h1}>✅ Payment Successful</h1>

        <p style={styles.storeLine}>
          {storeNumber ? `Store: ${storeNumber}` : "Store information missing from link"}
        </p>

        <p style={styles.p}>
          Your payment has been received.
          <br />
          Your membership is now being finalized.
        </p>

        <div style={styles.box}>
          <h3 style={styles.h3}>Check your inbox for an email from Avigilon Alta</h3>

          <p style={styles.pLeft}>
            You will receive an email from <strong>Avigilon Alta</strong> to set up your access.
            If you don’t see it, please check your Spam or Junk folder.
          </p>

          <img
            src="/assets/openpath-inbox.png"
            alt="Avigilon Alta email in inbox"
            style={styles.instructionImage}
          />
        </div>

        <div style={styles.box}>
          <h3 style={styles.h3}>Inside the email</h3>

          <ol style={styles.ol}>
            <li>Tap your App Store to download the Avigilon Alta app.</li>
            <li>Once installed, return to the email.</li>
            <li>
              Tap <strong>Activate Account</strong> and follow the prompts.
            </li>
          </ol>

          <img
            src="/assets/openpath-activate.png"
            alt="Avigilon Alta Activate Account email"
            style={styles.instructionImage}
          />
        </div>

        <div style={styles.box}>
          <h3 style={styles.h3}>Didn’t receive an email from Avigilon Alta?</h3>
          <p style={styles.pLeft}>
            Having trouble getting setup? Please submit the form and we&apos;ll reach out as soon as we can.
          </p>

          <form onSubmit={onSubmit}>
            <input
              style={styles.input}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              required
              disabled={submitting}
            />

            <input
              style={styles.input}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email"
              type="email"
              required
              disabled={submitting}
            />

            <input
              style={styles.input}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Phone (optional)"
              disabled={submitting}
            />

            <textarea
              style={{ ...styles.input, minHeight: 88, resize: "vertical" }}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional note"
              rows={3}
              disabled={submitting}
            />

            {/* Honeypot */}
            <input
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              autoComplete="off"
              tabIndex={-1}
              style={{ display: "none" }}
              aria-hidden="true"
            />

            <button type="submit" style={styles.button} disabled={submitting}>
              {submitting ? "Sending..." : "Request Help"}
            </button>

            {status.message ? (
              <div
                style={{
                  ...styles.status,
                  color: status.type === "success" ? "#2e7d32" : "#b00020",
                }}
              >
                {status.message}
              </div>
            ) : null}
          </form>
        </div>

        <div style={styles.footer}>Powered by Talvik</div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto',
    margin: 0,
    padding: 0,
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
  },

  container: {
    maxWidth: 600,
    width: "100%",
    margin: "80px 16px",
    background: "#ffffff",
    padding: 32,
    borderRadius: 12,
    boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
    textAlign: "center",
  },

  h1: { color: "#2e7d32", marginBottom: 8 },
  p: { color: "#444", lineHeight: 1.6, margin: "10px 0" },
  pLeft: { color: "#444", lineHeight: 1.6, margin: "10px 0" },
  storeLine: { color: "#777", marginTop: 0 },

  box: {
    marginTop: 22,
    paddingTop: 18,
    borderTop: "1px solid #e6efe8",
    textAlign: "left",
  },

  h3: { marginTop: 0, marginBottom: 8, color: "#2e7d32" },
  ol: { margin: "10px 0 0 18px", color: "#444", lineHeight: 1.6 },

  input: {
    width: "100%",
    padding: 12,
    margin: "6px 0",
    borderRadius: 8,
    border: "1px solid #ccc",
    fontSize: 14,
    boxSizing: "border-box",
  },

  button: {
    width: "100%",
    padding: 12,
    marginTop: 10,
    background: "#2e7d32",
    color: "white",
    border: "none",
    borderRadius: 10,
    fontSize: 16,
    cursor: "pointer",
  },

  status: { marginTop: 10, fontSize: 14 },

  footer: {
    marginTop: 30,
    paddingTop: 18,
    borderTop: "1px solid #e6efe8",
    fontSize: "0.9em",
    color: "#777",
  },

  instructionImage: {
    width: "100%",
    maxWidth: 420,
    margin: "12px auto 0",
    display: "block",
    borderRadius: 10,
    border: "1px solid #ddd",
  },
};
